import { CommonModule } from '@angular/common';
import {
  Component,
  signal,
  ChangeDetectionStrategy,
  inject,
  input,
  effect,
  computed,
} from '@angular/core';
import { CourseStepperComponent } from './course-stepper/course-stepper.component';
import { CourseSettingsComponent } from './course-settings/course-settings.component';
import {
  CourseSettingsData,
  DEFAULT_COURSE_CREATOR_STEPS,
  StepperStep,
} from '../../models';
import { CourseGenerateComponent } from './course-generate/course-generate.component';
import { CoursePublishComponent } from './course-publish/course-publish.component';
import { CourseAssignmentsComponent } from './course-assignments/course-assignments.component';
import { COURSE_CREATOR_BRIDGE } from './bridges/course-creator-bridge.token';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';
@Component({
  selector: 'app-course-creator',
  standalone: true,
  imports: [
    CommonModule,
    CourseStepperComponent,
    CourseSettingsComponent,
    CourseGenerateComponent,
    CoursePublishComponent,
    CourseAssignmentsComponent,
  ],
  templateUrl: './course-creator.component.html',
  styleUrl: './course-creator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseCreatorComponent {
  #bridge = inject(COURSE_CREATOR_BRIDGE);
  selectedCourse = input<ChallengeGen.Course | undefined>(undefined);
  initialStep = input<string>('settings');

  /**
   * Optional step definitions and order. When omitted, uses {@link DEFAULT_COURSE_CREATOR_STEPS}
   * (full school / My courses flow).
   */
  steps = input<StepperStep[] | undefined>(undefined);

  /** When set (e.g. admin content catalog), new courses are stored with VALVUR source. */
  courseCatalogSource = input<'VALVUR' | undefined>(undefined);

  /** `admin` uses POST /courses/valvur (topic, no file upload). `school` uses POST /courses. */
  layoutVariant = input<'school' | 'admin'>('school');

  readonly effectiveSteps = computed(
    () => this.steps() ?? DEFAULT_COURSE_CREATOR_STEPS,
  );

  readonly stepIds = computed(() => this.effectiveSteps().map((s) => s.id));

  /**
   * After publish, lock navigation to earlier steps (only for ids present in this flow).
   */
  readonly lockedStepsWhenPublished = computed(() => {
    const ids = new Set(this.stepIds());
    const candidates = ['settings', 'generate', 'publish'] as const;
    return candidates.filter((id) => ids.has(id));
  });

  currentStep = signal<string>('settings');
  completedSteps = signal<string[]>([]);

  constructor() {
    effect(() => {
      const requested = this.initialStep();
      const order = this.stepIds();
      const step = order.includes(requested)
        ? requested
        : (order[0] ?? 'settings');
      this.currentStep.set(step);

      const currentIndex = order.indexOf(step);
      if (currentIndex > 0) {
        this.completedSteps.set(order.slice(0, currentIndex));
      } else {
        this.completedSteps.set([]);
      }
    });
  }

  onStepClicked(stepId: string): void {
    if (
      ['DRAFT', 'READY_FOR_VIDEO_GENERATION'].includes(
        this.selectedCourse()?.status || '',
      )
    )
      return;
    if (!this.stepIds().includes(stepId)) return;
    this.currentStep.set(stepId);
  }

  onStepCompleted(stepId: string): void {
    const completed = this.completedSteps();
    if (!completed.includes(stepId)) {
      this.completedSteps.set([...completed, stepId]);
    }

    const nextStep = this.getNextStep(stepId);
    if (nextStep) {
      this.currentStep.set(nextStep);
    }
  }

  onSettingsDataChange(data: CourseSettingsData): void {
    if (this.layoutVariant() === 'admin') {
      const payload: Partial<CourseSettingsData> = {
        discipline: data.discipline,
        audience: data.audience,
        tone: data.tone,
        quantity: data.quantity,
        duration: data.duration,
        topic: (data.topic ?? '').trim(),
        courseSource: 'VALVUR',
        videoMainHeroName: data.videoMainHeroName,
        videoMainHeroDescription: data.videoMainHeroDescription ?? '',
        videoBackgroundHeroNames: data.videoBackgroundHeroNames ?? [],
      };
      this.#bridge.createAdminValvurDraft(payload as CourseSettingsData);
      return;
    }
    const catalog = this.courseCatalogSource();
    this.#bridge.createSchoolDraft({
      ...data,
      ...(catalog ? { courseSource: catalog } : {}),
    });
  }

  private getNextStep(currentStepId: string): string | null {
    const order = this.stepIds();
    const currentIndex = order.indexOf(currentStepId);
    if (currentIndex === -1 || currentIndex === order.length - 1) {
      return null;
    }
    return order[currentIndex + 1];
  }
}
